// Device chains, heard — approximately.
//
// Live's devices don't travel: the wire carries a chain's device NAMES and
// its parameters (name, value, range). What the browser can do with that is
// stand in a rough equivalent for the devices it recognises — a filter for
// Auto Filter, a compressor for Compressor, a waveshaper for Saturator, a
// synthetic room for Reverb, a delay line for Delay — driven by the Set's
// own parameter values, so a producer sweeping a knob in the browser hears
// the sweep move in the same direction as it will in Live. It is not Live's
// sound and never claims to be: anything unrecognised passes through, and
// the whole thing is one preference away from dry.
//
// Two halves: `stagesFor` is pure (a chain → stage descriptors, unit-tested);
// `buildGraph` turns stages into WebAudio nodes on any BaseAudioContext, so
// the live bus and the offline mixdown share one interpretation.

import type { DeviceParam, DeviceState } from "@shared/sync/state.js";

export type Stage =
  | { kind: "filter"; type: BiquadFilterType; frequency: number; q: number }
  | { kind: "eq3"; low: number; mid: number; high: number; lowFreq: number; highFreq: number }
  | { kind: "compressor"; threshold: number; ratio: number; attack: number; release: number; knee: number; makeup: number }
  | { kind: "shaper"; drive: number; mix: number }
  | { kind: "reverb"; decaySec: number; mix: number }
  | { kind: "delay"; timeSec: number; feedback: number; mix: number }
  | { kind: "gain"; gain: number };

/** Look a parameter up by any of several spellings, case-insensitively. */
function param(d: DeviceState, ...names: string[]): DeviceParam | undefined {
  const wanted = names.map((n) => n.toLowerCase());
  return d.params.find((p) => wanted.includes(p.name.toLowerCase()));
}

/** A parameter's position in its range, 0..1 — the unit-free reading. */
function norm(p: DeviceParam | undefined, fallback: number): number {
  if (!p || !(p.max > p.min)) return fallback;
  return Math.max(0, Math.min(1, (p.value - p.min) / (p.max - p.min)));
}

/** A value in decibels: taken as written when the range dips below zero
 * (Live's gains are dB), else read as a linear amount and converted. */
function decibels(p: DeviceParam | undefined, fallback: number): number {
  if (!p) return fallback;
  if (p.min < 0) return Math.max(-96, Math.min(36, p.value));
  return p.value > 0 ? Math.max(-96, Math.min(36, 20 * Math.log10(p.value))) : -96;
}

/** A time that may be in ms (Live's) or seconds — told apart by the RANGE:
 * a parameter that can reach past 30 counts in ms. A quantized one (Glue
 * Compressor's attack steps, a limiter's release modes) is an index into
 * names this model does not know: the fallback. */
function seconds(p: DeviceParam | undefined, fallback: number): number {
  if (!p || p.quantized) return fallback;
  const v = p.max > 30 ? p.value / 1000 : p.value;
  return Math.max(0, Math.min(60, v));
}

const FILTER_TYPES: Record<string, BiquadFilterType> = {
  lowpass: "lowpass",
  highpass: "highpass",
  bandpass: "bandpass",
  notch: "notch",
  morph: "bandpass",
};

function isOn(d: DeviceState): boolean {
  const on = param(d, "Device On");
  return on === undefined || on.value > 0.5;
}

/** The stages a chain reduces to, in order; unrecognised devices contribute none. */
export function stagesFor(devices: readonly DeviceState[] | undefined): Stage[] {
  const out: Stage[] = [];
  for (const d of devices ?? []) {
    if (!isOn(d)) continue;
    const stage = stageFor(d);
    if (stage) out.push(stage);
  }
  return out;
}

function stageFor(d: DeviceState): Stage | null {
  switch (d.name) {
    case "Auto Filter": {
      const type = param(d, "Filter Type", "Type");
      const item = type?.items?.[Math.round(type.value - type.min)]?.toLowerCase() ?? "lowpass";
      const freq = param(d, "Frequency", "Filter Freq", "Cutoff");
      return {
        kind: "filter",
        type: FILTER_TYPES[item] ?? "lowpass",
        frequency: freq ? Math.max(20, Math.min(20000, freq.value)) : 20000,
        q: 0.5 + norm(param(d, "Resonance", "Res"), 0.2) * 12,
      };
    }
    case "EQ Three": {
      const lo = param(d, "GainLo", "Low Gain");
      const mid = param(d, "GainMid", "Mid Gain");
      const hi = param(d, "GainHi", "High Gain");
      const fLo = param(d, "FreqLo", "Low Freq");
      const fHi = param(d, "FreqHi", "High Freq");
      return {
        kind: "eq3",
        low: decibels(lo, 0),
        mid: decibels(mid, 0),
        high: decibels(hi, 0),
        lowFreq: fLo ? Math.max(50, Math.min(5000, fLo.value)) : 250,
        highFreq: fHi ? Math.max(500, Math.min(15000, fHi.value)) : 2500,
      };
    }
    case "Channel EQ":
      return {
        kind: "eq3",
        low: decibels(param(d, "Low Gain"), 0),
        mid: decibels(param(d, "Mid Gain"), 0),
        high: decibels(param(d, "High Gain"), 0),
        lowFreq: 200,
        highFreq: 4000,
      };
    case "Compressor":
    case "Glue Compressor": {
      const ratio = param(d, "Ratio");
      return {
        kind: "compressor",
        threshold: Math.max(-60, Math.min(0, decibels(param(d, "Threshold"), -18))),
        ratio: ratio ? Math.max(1, Math.min(20, ratio.value)) : 4,
        attack: seconds(param(d, "Attack"), 0.01),
        release: seconds(param(d, "Release"), 0.1),
        knee: Math.max(0, Math.min(40, param(d, "Knee")?.value ?? 6)),
        makeup: Math.max(-24, Math.min(24, decibels(param(d, "Output Gain", "Makeup", "Output"), 0))),
      };
    }
    case "Limiter":
      return {
        kind: "compressor",
        threshold: Math.max(-60, Math.min(0, decibels(param(d, "Ceiling"), -0.3))),
        ratio: 20,
        attack: 0.001,
        release: seconds(param(d, "Release"), 0.05),
        knee: 0,
        makeup: Math.max(-24, Math.min(24, decibels(param(d, "Gain"), 0))),
      };
    case "Saturator":
    case "Overdrive":
    case "Pedal":
    case "Roar":
    case "Vinyl Distortion":
    case "Erosion":
    case "Redux":
    case "Drum Buss": {
      const drive = param(d, "Drive", "Amount");
      return { kind: "shaper", drive: norm(drive, 0.2), mix: norm(param(d, "Dry/Wet", "Mix"), 1) };
    }
    case "Reverb":
    case "Hybrid Reverb":
      return {
        kind: "reverb",
        decaySec: Math.max(0.1, Math.min(12, seconds(param(d, "Decay Time", "Decay"), 1.2))),
        mix: norm(param(d, "Dry/Wet", "Mix"), 0.3),
      };
    case "Delay":
    case "Echo":
    case "Filter Delay":
    case "Grain Delay":
      return {
        kind: "delay",
        timeSec: Math.max(0.005, Math.min(5, seconds(param(d, "L Time", "Delay Time", "Time", "L Delay Time"), 0.375))),
        feedback: Math.min(0.95, norm(param(d, "Feedback", "L Feedback"), 0.3)),
        mix: norm(param(d, "Dry/Wet", "Mix"), 0.3),
      };
    case "Utility": {
      const mute = param(d, "Mute");
      if (mute && mute.value > 0.5) return { kind: "gain", gain: 0 };
      return { kind: "gain", gain: Math.pow(10, decibels(param(d, "Gain"), 0) / 20) };
    }
    default:
      return null;
  }
}

export interface DeviceGraph {
  input: AudioNode;
  output: AudioNode;
  /** Move the parameters to `stages` in place when the stage KINDS line up
   * (a knob turned, a mix moved) — reverb and delay tails ride through,
   * compressors keep their state. False when the shape differs, and the
   * caller rebuilds. */
  update(stages: readonly Stage[]): boolean;
  dispose(): void;
}

/** Impulses by (rate, decay to 10 ms) — a 12 s room is a million samples,
 * not something to synthesise again for every knob on the same lane. */
const impulses = new Map<string, AudioBuffer>();
const IMPULSE_CACHE_MAX = 12;

/** A synthetic room: exponentially decaying noise, a little late bloom. */
function impulse(ctx: BaseAudioContext, decaySec: number): AudioBuffer {
  const rate = ctx.sampleRate;
  const key = `${rate}:${Math.round(decaySec * 100)}`;
  const cached = impulses.get(key);
  if (cached) return cached;
  const built = synthesiseImpulse(ctx, decaySec);
  if (impulses.size >= IMPULSE_CACHE_MAX) impulses.delete(impulses.keys().next().value!);
  impulses.set(key, built);
  return built;
}

function synthesiseImpulse(ctx: BaseAudioContext, decaySec: number): AudioBuffer {
  const rate = ctx.sampleRate;
  const frames = Math.max(1, Math.round(decaySec * rate));
  const buf = ctx.createBuffer(2, frames, rate);
  for (let ch = 0; ch < 2; ch++) {
    const data = buf.getChannelData(ch);
    let seed = 0x9e3779b9 + ch;
    for (let i = 0; i < frames; i++) {
      seed = (seed * 1664525 + 1013904223) >>> 0;
      const white = (seed / 0xffffffff) * 2 - 1;
      const t = i / frames;
      data[i] = white * Math.pow(1 - t, 2.5) * (i < 200 ? i / 200 : 1);
    }
  }
  return buf;
}

function shaperCurve(drive: number): Float32Array<ArrayBuffer> {
  const n = 1024;
  const curve = new Float32Array(new ArrayBuffer(n * 4));
  const k = 1 + drive * 40;
  for (let i = 0; i < n; i++) {
    const x = (i / (n - 1)) * 2 - 1;
    curve[i] = Math.tanh(x * k) / Math.tanh(k);
  }
  return curve;
}

/** The nodes a built stage exposes for in-place updates. */
type Built =
  | { kind: "filter"; node: BiquadFilterNode }
  | { kind: "eq3"; lo: BiquadFilterNode; mid: BiquadFilterNode; hi: BiquadFilterNode }
  | { kind: "compressor"; node: DynamicsCompressorNode; makeup: GainNode }
  | { kind: "shaper"; shaper: WaveShaperNode; trim: GainNode; dry: GainNode; wet: GainNode; drive: number }
  | { kind: "reverb"; conv: ConvolverNode; dry: GainNode; wet: GainNode; decaySec: number }
  | { kind: "delay"; delay: DelayNode; fb: GainNode; dry: GainNode; wet: GainNode }
  | { kind: "gain"; node: GainNode };

/** A dry/wet split around a wet path: `wetIn → … → wetOut`. */
function mixed(ctx: BaseAudioContext, mix: number, wire: (wetIn: AudioNode) => AudioNode): { input: AudioNode; output: AudioNode; dry: GainNode; wet: GainNode } {
  const input = ctx.createGain();
  const output = ctx.createGain();
  const dry = ctx.createGain();
  const wet = ctx.createGain();
  dry.gain.value = 1 - mix;
  wet.gain.value = mix;
  input.connect(dry);
  dry.connect(output);
  const wetOut = wire(input);
  wetOut.connect(wet);
  wet.connect(output);
  return { input, output, dry, wet };
}

function applyStage(ctx: BaseAudioContext, b: Built, s: Stage): void {
  switch (s.kind) {
    case "filter":
      if (b.kind !== "filter") return;
      b.node.type = s.type;
      b.node.frequency.value = s.frequency;
      b.node.Q.value = s.q;
      return;
    case "eq3":
      if (b.kind !== "eq3") return;
      b.lo.frequency.value = s.lowFreq;
      b.lo.gain.value = s.low;
      b.mid.frequency.value = Math.sqrt(s.lowFreq * s.highFreq);
      b.mid.gain.value = s.mid;
      b.hi.frequency.value = s.highFreq;
      b.hi.gain.value = s.high;
      return;
    case "compressor":
      if (b.kind !== "compressor") return;
      b.node.threshold.value = s.threshold;
      b.node.ratio.value = s.ratio;
      b.node.attack.value = Math.min(1, s.attack);
      b.node.release.value = Math.min(1, s.release);
      b.node.knee.value = s.knee;
      b.makeup.gain.value = Math.pow(10, s.makeup / 20);
      return;
    case "shaper":
      if (b.kind !== "shaper") return;
      if (b.drive !== s.drive) {
        b.shaper.curve = shaperCurve(s.drive);
        b.drive = s.drive;
      }
      b.trim.gain.value = 1 / (1 + s.drive * 2);
      b.dry.gain.value = 1 - s.mix;
      b.wet.gain.value = s.mix;
      return;
    case "reverb":
      if (b.kind !== "reverb") return;
      if (b.decaySec !== s.decaySec) {
        b.conv.buffer = impulse(ctx, s.decaySec);
        b.decaySec = s.decaySec;
      }
      b.dry.gain.value = 1 - s.mix;
      b.wet.gain.value = s.mix;
      return;
    case "delay":
      if (b.kind !== "delay") return;
      b.delay.delayTime.value = s.timeSec;
      b.fb.gain.value = s.feedback;
      b.dry.gain.value = 1 - s.mix;
      b.wet.gain.value = s.mix;
      return;
    case "gain":
      if (b.kind !== "gain") return;
      b.node.gain.value = s.gain;
      return;
  }
}

/** Build the stages as nodes on `ctx`; an empty chain is a straight wire. */
export function buildGraph(ctx: BaseAudioContext, stages: readonly Stage[]): DeviceGraph {
  const input = ctx.createGain();
  const nodes: AudioNode[] = [input];
  const built: Built[] = [];
  let tail: AudioNode = input;
  const link = (next: { input: AudioNode; output: AudioNode }): void => {
    tail.connect(next.input);
    tail = next.output;
    nodes.push(next.input, next.output);
  };
  for (const s of stages) {
    switch (s.kind) {
      case "filter": {
        const f = ctx.createBiquadFilter();
        link({ input: f, output: f });
        built.push({ kind: "filter", node: f });
        break;
      }
      case "eq3": {
        const lo = ctx.createBiquadFilter();
        lo.type = "lowshelf";
        const mid = ctx.createBiquadFilter();
        mid.type = "peaking";
        mid.Q.value = 0.7;
        const hi = ctx.createBiquadFilter();
        hi.type = "highshelf";
        lo.connect(mid);
        mid.connect(hi);
        link({ input: lo, output: hi });
        built.push({ kind: "eq3", lo, mid, hi });
        break;
      }
      case "compressor": {
        const c = ctx.createDynamicsCompressor();
        const makeup = ctx.createGain();
        c.connect(makeup);
        link({ input: c, output: makeup });
        built.push({ kind: "compressor", node: c, makeup });
        break;
      }
      case "shaper": {
        const w = ctx.createWaveShaper();
        w.oversample = "2x";
        const trim = ctx.createGain();
        const m = mixed(ctx, s.mix, (wetIn) => {
          wetIn.connect(w);
          w.connect(trim);
          return trim;
        });
        link(m);
        built.push({ kind: "shaper", shaper: w, trim, dry: m.dry, wet: m.wet, drive: -1 });
        break;
      }
      case "reverb": {
        const conv = ctx.createConvolver();
        const m = mixed(ctx, s.mix, (wetIn) => {
          wetIn.connect(conv);
          return conv;
        });
        link(m);
        built.push({ kind: "reverb", conv, dry: m.dry, wet: m.wet, decaySec: -1 });
        break;
      }
      case "delay": {
        const delay = ctx.createDelay(5);
        const fb = ctx.createGain();
        const m = mixed(ctx, s.mix, (wetIn) => {
          wetIn.connect(delay);
          delay.connect(fb);
          fb.connect(delay);
          return delay;
        });
        link(m);
        built.push({ kind: "delay", delay, fb, dry: m.dry, wet: m.wet });
        break;
      }
      case "gain": {
        const g = ctx.createGain();
        link({ input: g, output: g });
        built.push({ kind: "gain", node: g });
        break;
      }
    }
  }
  built.forEach((b, i) => applyStage(ctx, b, stages[i]!));
  return {
    input,
    output: tail,
    update: (next) => {
      if (next.length !== built.length || next.some((s, i) => s.kind !== built[i]!.kind)) return false;
      built.forEach((b, i) => applyStage(ctx, b, next[i]!));
      return true;
    },
    dispose: () => {
      for (const n of nodes) {
        try {
          n.disconnect();
        } catch {
          // already detached
        }
      }
    },
  };
}
